import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, X, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  CLIENTS_WIDGET_IDS,
  GRID_COLS,
  GRID_ROWS,
  validateNoOverlap,
  type WidgetLayout,
} from '@proadv/types';

import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { AddWidgetMenu } from '@/components/dashboard/add-widget-menu';
import { DashboardGrid } from '@/components/dashboard/dashboard-grid';
import { CLIENTS_DEFAULT_LAYOUT } from '@/components/dashboard/clients-widgets/default-layout';
import { ClientsList } from '@/components/clients/clients-list';

const KNOWN = new Set<string>(CLIENTS_WIDGET_IDS);

function sanitizeLayout(
  input: WidgetLayout[] | null | undefined,
): WidgetLayout[] {
  if (!input || input.length === 0) return CLIENTS_DEFAULT_LAYOUT;
  const out = input
    .filter((w) => KNOWN.has(w.widgetId))
    .map((w) => ({
      widgetId: w.widgetId,
      x: Math.max(0, Math.min(GRID_COLS - 1, Math.floor(w.x))),
      y: Math.max(0, Math.min(GRID_ROWS - 1, Math.floor(w.y))),
      w: Math.max(1, Math.min(4, Math.floor(w.w))),
      h: Math.max(1, Math.min(3, Math.floor(w.h))),
    }));
  return out.length > 0 ? out : CLIENTS_DEFAULT_LAYOUT;
}

function findFreeSpot(layout: WidgetLayout[]): { x: number; y: number } {
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      const tentative: WidgetLayout = {
        widgetId: '__probe__',
        x,
        y,
        w: 1,
        h: 1,
      };
      const conflict = validateNoOverlap([...layout, tentative]);
      if (!conflict) return { x, y };
    }
  }
  return { x: 0, y: GRID_ROWS - 1 };
}

export default function ClientesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draftLayout, setDraftLayout] = useState<WidgetLayout[] | null>(null);

  const layoutQuery = useQuery({
    queryKey: ['me', 'dashboard-layout', 'clients'],
    queryFn: () => apiClient.meGetDashboardLayout('clients'),
    staleTime: 1000 * 60,
  });

  const savedLayout = useMemo(
    () => sanitizeLayout(layoutQuery.data?.layout),
    [layoutQuery.data],
  );

  const activeLayout =
    mode === 'edit' && draftLayout ? draftLayout : savedLayout;

  const saveMutation = useMutation({
    mutationFn: (layout: WidgetLayout[]) =>
      apiClient.meUpdateDashboardLayout('clients', layout),
    onSuccess: (res) => {
      queryClient.setQueryData(['me', 'dashboard-layout', 'clients'], {
        layout: res.layout,
      });
      setMode('view');
      setDraftLayout(null);
    },
  });

  function enterEditMode() {
    setDraftLayout(savedLayout.map((w) => ({ ...w })));
    setMode('edit');
  }

  function cancelEdit() {
    setMode('view');
    setDraftLayout(null);
    saveMutation.reset();
  }

  function saveEdit() {
    if (!draftLayout) return;
    const overlap = validateNoOverlap(draftLayout);
    if (overlap) return;
    saveMutation.mutate(draftLayout);
  }

  function handleLayoutChange(next: WidgetLayout[]) {
    if (mode !== 'edit') return;
    setDraftLayout(next);
  }

  function handleRemove(widgetId: string) {
    if (mode !== 'edit') return;
    setDraftLayout((prev) =>
      (prev ?? savedLayout).filter((w) => w.widgetId !== widgetId),
    );
  }

  function handleAdd(widgetId: string) {
    setDraftLayout((prev) => {
      const base = prev ?? savedLayout;
      if (base.some((w) => w.widgetId === widgetId)) return base;
      const spot = findFreeSpot(base);
      return [...base, { widgetId, x: spot.x, y: spot.y, w: 1, h: 1 }];
    });
  }

  useEffect(() => {
    if (mode !== 'edit') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (
        (e.key === 's' || e.key === 'S') &&
        (e.ctrlKey || e.metaKey)
      ) {
        e.preventDefault();
        saveEdit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, draftLayout]);

  const presentIds = activeLayout.map((w) => w.widgetId);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => navigate('/escritorio/clientes/novo')}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition',
              'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </button>
          <button
            type="button"
            onClick={() => (mode === 'edit' ? cancelEdit() : enterEditMode())}
            aria-label={
              mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'
            }
            title={
              mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'
            }
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md border transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              mode === 'edit' && 'text-[var(--accent)]',
            )}
          >
            {mode === 'edit' ? (
              <X className="h-4 w-4" />
            ) : (
              <LayoutGrid className="h-4 w-4" />
            )}
          </button>
        </div>
      </header>

      {mode === 'edit' && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2',
            'border-[var(--accent)]/40 bg-[var(--accent)]/5',
          )}
        >
          <p className="text-xs text-[var(--fg-muted)]">
            Modo edição: arraste para mover, alças para redimensionar.
            <span className="ml-2 opacity-60">Esc cancela · Ctrl+S salva</span>
          </p>
          <div className="flex items-center gap-2">
            <AddWidgetMenu
              pageId="clients"
              presentIds={presentIds}
              onAdd={handleAdd}
            />
            <button
              type="button"
              onClick={cancelEdit}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              )}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saveMutation.isPending}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition',
                'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
                saveMutation.isPending && 'opacity-60',
              )}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-xs text-red-500">
          Falha ao salvar o layout. Tente novamente.
        </p>
      )}

      <DashboardGrid
        pageId="clients"
        layout={activeLayout}
        mode={mode}
        onLayoutChange={handleLayoutChange}
        onRemove={handleRemove}
      />

      <div className="border-t border-[var(--border)]" />

      <ClientsList />
    </div>
  );
}
